import { useMemo } from 'react';

import { useRelationshipsContext } from '../providers/RelationshipsProvider';
import { Relationship, RelationshipsData } from '../types/types';

interface UseAllRelationshipsDataReturn {
  data: Relationship[];
  loading: boolean;
  error: boolean;
}

/**
 * This hook allows getting relationship service data.
 * It must be used inside `RelationshipsProvider` component.
 *
 * The returned object is of shape:
 *   {
 *        data:[{
 *                  id: '123',
 *                  entities: [{
 *                    id: '456',
 *                    domain: 'autodesk-bim360-documentmanagement',
 *                    type: 'documentversion'
 *                  }, {
 *                    id: '789',
 *                    domain: 'autodesk-construction-sheet',
 *                    type: 'sheetversion'
 *                  }]
 *        }],
 *        error: undefined,
 *        loading: false
 *   }
 * @returns {{loading: *, error: *, data: *}}
 */

export default (): UseAllRelationshipsDataReturn => {
  const { relationshipDataBySource, relationshipsEntityMap } =
    useRelationshipsContext();
  return useMemo(() => {
    const results: Relationship[] = [];
    let loading = false;
    let error = false;

    const processRelationshipsData = (
      relationshipsData: RelationshipsData[],
      isCurrentLoading?: boolean,
      currentError?: boolean,
    ) => {
      if (relationshipsData?.length) {
        relationshipsData.forEach((relationship) => {
          const entity = relationshipsEntityMap[relationship.relationshipId];
          if (entity) {
            // eslint-disable-next-line better-mutation/no-mutating-methods -- local accumulator array built within this hook
            results.push(entity);
          }
        });
      }
      if (isCurrentLoading) loading = true;
      if (currentError) error = currentError;
    };

    Object.keys(relationshipDataBySource).forEach((domain) => {
      Object.keys(relationshipDataBySource[domain]).forEach((type) => {
        Object.keys(relationshipDataBySource[domain][type]).forEach((id) => {
          const {
            relationshipsData,
            loading: isCurrentLoading,
            error: currentError,
          } = relationshipDataBySource[domain][type][id];
          processRelationshipsData(
            relationshipsData,
            isCurrentLoading,
            currentError,
          );
        });
      });
    });
    return {
      data: results,
      loading,
      error,
    };
  }, [relationshipDataBySource, relationshipsEntityMap]);
};
